import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type Skill = components['schemas']['SkillResponse'];
export type UserSkill = components['schemas']['UserSkillResponse'];
export type SkillProficiency = components['schemas']['SkillProficiency'];

/**
 * The proficiencies the API accepts, as a value the UI can iterate to build a
 * `<select>`. `satisfies` pins each entry to a real member of the generated
 * union, so a typo or a REMOVED level is a type error — the same trick, and
 * the same caveat, as `LICENSE_CLASSES` (M1-37): a level ADDED on the API side
 * still compiles green here and simply never appears.
 */
export const SKILL_PROFICIENCIES = [
  'Basic',
  'Proficient',
  'Expert',
] as const satisfies readonly SkillProficiency[];

/** Thrown for the 403 a technician gets when looking at someone ELSE's skills. */
export class UserSkillsNotPermittedError extends Error {
  constructor() {
    super('Not permitted to view these skills');
    this.name = 'UserSkillsNotPermittedError';
  }
}

/** Thrown for the 400 the API returns for a label another active skill already holds. */
export class DuplicateSkillLabelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DuplicateSkillLabelError';
  }
}

const CATALOG_KEY = ['skills'] as const;

function userSkillsQueryKey(userId: string) {
  return ['users', userId, 'skills'] as const;
}

/**
 * The account's skill catalog (M1-38). `users.read`-gated, so a Technician
 * never calls it — the labels of the skills THEY hold ride along on their own
 * assignments instead (see {@link useUserSkills}), which is the only reason a
 * technician's own profile page can render without this.
 *
 * `includeRetired` is for the management screen only; every picker wants
 * active entries, which is why that is the default on the API side rather than
 * something each caller has to remember to ask for.
 */
export function useSkills(includeRetired = false, enabled = true) {
  return useQuery({
    queryKey: [...CATALOG_KEY, { includeRetired }] as const,
    enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/skills', {
        params: { query: { includeRetired } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /skills failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

function invalidateCatalog(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: CATALOG_KEY });
  // A rename or a retire changes what every ASSIGNMENT renders — the label and
  // the retired flag are carried on the assignment row, so a catalog edit that
  // only refreshed the catalog would leave a stale label on somebody's profile.
  void queryClient.invalidateQueries({ queryKey: ['users'] });
}

/**
 * The server's own wording for a taken label, so the office reads the rule the
 * API actually applied (case-insensitive, among ACTIVE entries only) rather
 * than a client-side paraphrase that can drift from it.
 *
 * ⚠️ Read from openapi-fetch's `error`, NOT by re-reading the `Response` —
 * openapi-fetch has already consumed the body by the time it hands it back, so
 * `response.clone().json()` throws and silently yields the fallback. The same
 * "a 4xx body is read from `error`, not from the response" lesson
 * web/CONVENTIONS.md's M1-05 section records for a 409; found here by a test
 * asserting the specific message and getting the generic one.
 */
function duplicateLabelMessage(error: unknown, fallback: string): string {
  const problem = error as { errors?: Record<string, string[]> } | undefined;
  return problem?.errors?.['Label']?.[0] ?? fallback;
}

export function useCreateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const { data, error, response } = await apiClient.POST('/api/v1/skills', { body: { label } });
      if (response.status === 400) {
        throw new DuplicateSkillLabelError(
          duplicateLabelMessage(error, 'That skill could not be added.'),
        );
      }
      if (!response.ok || !data) {
        throw new Error(`POST /skills failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateCatalog(queryClient),
  });
}

export function useUpdateSkill() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, label, sortOrder }: { id: string; label: string; sortOrder: number }) => {
      const { data, error, response } = await apiClient.PUT('/api/v1/skills/{id}', {
        params: { path: { id } },
        body: { label, sortOrder },
      });
      if (response.status === 400) {
        throw new DuplicateSkillLabelError(
          duplicateLabelMessage(error, 'That skill could not be renamed.'),
        );
      }
      if (!response.ok || !data) {
        throw new Error(`PUT /skills/{id} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateCatalog(queryClient),
  });
}

export function useSetSkillRetired() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, retired }: { id: string; retired: boolean }) => {
      // Two routes, not one with a flag — the API's own shape (M1-25's
      // activate/deactivate pair). ⚠️ Both are bodyless POSTs whose URL also
      // contains `/skills`, so a hand-rolled test stub has to match them
      // BEFORE the generic create route or they get swallowed: the
      // most-specific-first rule web/CONVENTIONS.md records twice already.
      const path = retired ? '/api/v1/skills/{id}/retire' : '/api/v1/skills/{id}/restore';
      const { data, response } = await apiClient.POST(path, { params: { path: { id } } });
      if (!response.ok || !data) {
        throw new Error(`POST ${path} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => invalidateCatalog(queryClient),
  });
}

/**
 * One person's skills (M1-38).
 *
 * Takes a USER id, not a profile id — unlike {@link useTechnicianLicenses},
 * and that is this story's central decision rather than an inconsistency
 * (api/CONVENTIONS.md's M1-38 section). The practical consequence on this
 * screen: it needs no `enabled` gate on a profile existing, so somebody with
 * no technician profile at all still has skills that render.
 *
 * The route is bare-authenticated with an ownership check in the handler, so a
 * Technician CAN call it for themselves. The 403 (someone else's) gets its own
 * error type so the page can render a refusal rather than a load failure.
 */
export function useUserSkills(userId: string | undefined) {
  return useQuery({
    queryKey: userSkillsQueryKey(userId ?? ''),
    enabled: userId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/users/{userId}/skills', {
        params: { path: { userId: userId! } },
      });
      if (response.status === 403) {
        throw new UserSkillsNotPermittedError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /users/{userId}/skills failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export function useAssignUserSkill(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ skillId, proficiency }: { skillId: string; proficiency: SkillProficiency }) => {
      const { data, response } = await apiClient.POST('/api/v1/users/{userId}/skills', {
        params: { path: { userId } },
        body: { skillId, proficiency },
      });
      if (!response.ok || !data) {
        throw new Error(`POST /users/{userId}/skills failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userSkillsQueryKey(userId) }),
  });
}

export function useUpdateUserSkill(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ skillId, proficiency }: { skillId: string; proficiency: SkillProficiency }) => {
      const { data, response } = await apiClient.PUT('/api/v1/users/{userId}/skills/{skillId}', {
        params: { path: { userId, skillId } },
        body: { proficiency },
      });
      if (!response.ok || !data) {
        throw new Error(`PUT /users/{userId}/skills/{skillId} failed: ${response.status}`);
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userSkillsQueryKey(userId) }),
  });
}

export function useRemoveUserSkill(userId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (skillId: string) => {
      const { response } = await apiClient.DELETE('/api/v1/users/{userId}/skills/{skillId}', {
        params: { path: { userId, skillId } },
      });
      // 204, so there is no body to check — `!data` would reject the success case.
      if (!response.ok) {
        throw new Error(`DELETE /users/{userId}/skills/{skillId} failed: ${response.status}`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: userSkillsQueryKey(userId) }),
  });
}
